// src/data/onboardingSteps.tsx
// Role-specific onboarding tour step definitions
import React from 'react';
import { Step } from 'react-joyride';
import { Box, Typography } from '@mui/material';
import { appTheme, fontFamily } from '../styles/theme';
import { ROUTES } from '../constants';

type TFunction = (key: string, options?: any) => string;

const makeWelcome = (role: string, t: TFunction, route: string): Step & { route: string } => ({
  target: 'body',
  placement: 'center' as const,
  content: (
    <Box sx={{ textAlign: 'center', p: 1 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, fontFamily, color: appTheme.colors.primary.main }}>
        {t(`onboarding.${role}.welcome.title`)}
      </Typography>
      <Typography variant="body1" sx={{ color: appTheme.colors.text.secondary, fontFamily, lineHeight: 1.7 }}>
        {t(`onboarding.${role}.welcome.description`)}
      </Typography>
    </Box>
  ),
  route,
});

const makeComplete = (role: string, t: TFunction, route: string): Step & { route: string } => ({
  target: 'body',
  placement: 'center' as const,
  content: (
    <Box sx={{ textAlign: 'center', p: 1 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, fontFamily, color: '#10b981' }}>
        {t(`onboarding.${role}.complete.title`)}
      </Typography>
      <Typography variant="body1" sx={{ color: appTheme.colors.text.secondary, fontFamily }}>
        {t(`onboarding.${role}.complete.description`)}
      </Typography>
    </Box>
  ),
  route,
});

const makeSidebarStep = (
  target: string,
  role: string,
  stepKey: string,
  t: TFunction,
  isRTL: boolean,
  route: string
): Step & { route: string } => ({
  target: `[data-tour="${target}"]`,
  placement: (isRTL ? 'left' : 'right') as any,
  content: (
    <Box sx={{ p: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
        {t(`onboarding.${role}.${stepKey}.title`)}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily }}>
        {t(`onboarding.${role}.${stepKey}.description`)}
      </Typography>
    </Box>
  ),
  route,
});

// ─── Restaurant Owner Steps ──────────────────────────────────────────
function getOwnerSteps(t: TFunction, isRTL: boolean): (Step & { route: string })[] {
  return [
    makeWelcome('owner', t, ROUTES.RESTAURANT_DASHBOARD),
    makeSidebarStep('sidebar-settings', 'owner', 'settings', t, isRTL, ROUTES.RESTAURANT_DASHBOARD),
    makeSidebarStep('sidebar-staff', 'owner', 'staff', t, isRTL, ROUTES.RESTAURANT_STAFF),
    makeSidebarStep('sidebar-menu', 'owner', 'menu', t, isRTL, ROUTES.RESTAURANT_CATEGORIES),
    makeSidebarStep('sidebar-inventory', 'owner', 'inventory', t, isRTL, ROUTES.RESTAURANT_INVENTORY),
    makeSidebarStep('sidebar-delivery', 'owner', 'delivery', t, isRTL, ROUTES.RESTAURANT_DELIVERY_COMPANIES),
    makeSidebarStep('sidebar-pos', 'owner', 'pos', t, isRTL, ROUTES.RESTAURANT_CASHIER),
    makeSidebarStep('sidebar-reports', 'owner', 'reports', t, isRTL, ROUTES.RESTAURANT_REPORTS),
    makeComplete('owner', t, ROUTES.RESTAURANT_SETTINGS),
  ];
}

// ─── Cashier Steps ──────────────────────────────────────────────────
function getCashierSteps(t: TFunction, isRTL: boolean): (Step & { route: string })[] {
  return [
    makeWelcome('cashier', t, ROUTES.RESTAURANT_CASHIER),
    makeSidebarStep('sidebar-pos', 'cashier', 'pos', t, isRTL, ROUTES.RESTAURANT_CASHIER),
    makeSidebarStep('sidebar-orders', 'cashier', 'orders', t, isRTL, ROUTES.RESTAURANT_ORDERS),
    makeSidebarStep('sidebar-tables', 'cashier', 'tables', t, isRTL, ROUTES.RESTAURANT_TABLES),
    makeSidebarStep('sidebar-scheduling', 'cashier', 'scheduling', t, isRTL, ROUTES.RESTAURANT_SCHEDULING),
    makeComplete('cashier', t, ROUTES.RESTAURANT_CASHIER),
  ];
}

// ─── Accountant Steps ───────────────────────────────────────────────
function getAccountantSteps(t: TFunction, isRTL: boolean): (Step & { route: string })[] {
  return [
    makeWelcome('accountant', t, ROUTES.RESTAURANT_ACCOUNTANT),
    makeSidebarStep('sidebar-accountant', 'accountant', 'dashboard', t, isRTL, ROUTES.RESTAURANT_ACCOUNTANT),
    makeSidebarStep('sidebar-expenses', 'accountant', 'expenses', t, isRTL, ROUTES.RESTAURANT_EXPENSES),
    makeSidebarStep('sidebar-cash-register', 'accountant', 'cashRegister', t, isRTL, ROUTES.RESTAURANT_CASH_REGISTER),
    makeSidebarStep('sidebar-reports', 'accountant', 'reports', t, isRTL, ROUTES.RESTAURANT_REPORTS),
    makeSidebarStep('sidebar-delivery-companies', 'accountant', 'deliveryCompanies', t, isRTL, ROUTES.RESTAURANT_DELIVERY_COMPANIES),
    makeSidebarStep('sidebar-inventory', 'accountant', 'inventory', t, isRTL, ROUTES.RESTAURANT_INVENTORY),
    makeComplete('accountant', t, ROUTES.RESTAURANT_ACCOUNTANT),
  ];
}

// ─── Inventory Manager Steps ────────────────────────────────────────
function getInventoryManagerSteps(t: TFunction, isRTL: boolean): (Step & { route: string })[] {
  return [
    makeWelcome('inventoryManager', t, ROUTES.RESTAURANT_INVENTORY),
    makeSidebarStep('sidebar-inventory', 'inventoryManager', 'inventory', t, isRTL, ROUTES.RESTAURANT_INVENTORY),
    makeSidebarStep('sidebar-templates', 'inventoryManager', 'templates', t, isRTL, ROUTES.RESTAURANT_TEMPLATES),
    makeSidebarStep('sidebar-notifications', 'inventoryManager', 'notifications', t, isRTL, ROUTES.RESTAURANT_NOTIFICATIONS),
    makeComplete('inventoryManager', t, ROUTES.RESTAURANT_INVENTORY),
  ];
}

// ─── Delivery Cashier Steps ─────────────────────────────────────────
function getDeliveryCashierSteps(t: TFunction, isRTL: boolean): (Step & { route: string })[] {
  return [
    makeWelcome('deliveryCashier', t, ROUTES.RESTAURANT_CASHIER),
    makeSidebarStep('sidebar-pos', 'deliveryCashier', 'pos', t, isRTL, ROUTES.RESTAURANT_CASHIER),
    makeSidebarStep('sidebar-delivery-orders', 'deliveryCashier', 'deliveryOrders', t, isRTL, ROUTES.RESTAURANT_DELIVERY_ORDERS),
    makeComplete('deliveryCashier', t, ROUTES.RESTAURANT_CASHIER),
  ];
}

// ─── Main Factory Function ──────────────────────────────────────────
export function getStepsForRole(
  role: string,
  t: TFunction,
  isRTL: boolean
): (Step & { route: string })[] {
  switch (role) {
    case 'restaurant_owner':
      return getOwnerSteps(t, isRTL);
    case 'cashier':
      return getCashierSteps(t, isRTL);
    case 'accountant':
      return getAccountantSteps(t, isRTL);
    case 'inventory_manager':
      return getInventoryManagerSteps(t, isRTL);
    case 'delivery_cashier':
      return getDeliveryCashierSteps(t, isRTL);
    default:
      return getOwnerSteps(t, isRTL); // fallback
  }
}

// ─── Dashboard Route Per Role ───────────────────────────────────────
export function getDashboardForRole(role: string): string {
  switch (role) {
    case 'restaurant_owner':
      return ROUTES.RESTAURANT_DASHBOARD;
    case 'cashier':
      return ROUTES.RESTAURANT_CASHIER;
    case 'accountant':
      return ROUTES.RESTAURANT_ACCOUNTANT;
    case 'inventory_manager':
      return ROUTES.RESTAURANT_INVENTORY;
    case 'delivery_cashier':
      return ROUTES.RESTAURANT_CASHIER;
    case 'waiter':
      return ROUTES.RESTAURANT_CASHIER;
    default:
      return ROUTES.RESTAURANT_DASHBOARD;
  }
}
